/**
 * The review loop (`/flashcards/review`). State lives in memory and `sessionStorage` (see
 * `session.ts`) so a refresh survives. This file owns the session: the queue bootstrap for
 * `?start=mixed`, the review request, the waiting countdown for "Igen" cards and the screens
 * around the card. One card's typed recall, flip, focus and keys are `ReviewStep.tsx`; the card
 * itself is `ReviewCard.tsx`.
 *
 * `?start=mixed&limit=N&returnTo=…` fetches the mixed queue (every moment), stores it as the
 * session and strips the URL; `returnTo` is kept only if `safeReturnTo` accepts it and puts a
 * "Tillbaka till dagens pass" button on the summary.
 */
import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { RELEARN_DELAY_MS, type Rating } from "@shared/sm2";
import { ProgressBar, Spinner, useToast } from "@client/components/ui";
import { useT } from "@client/i18n";
import { messageForError } from "@client/lib/errors";
import { useReviewCard, useStartQueue } from "./api";
import { CardBadges } from "./CardBadges";
import { QueuePanel } from "./QueuePanel";
import { mixedLimit, safeReturnTo } from "./returnTo";
import { ReviewStep } from "./ReviewStep";
import { EmptyView, SummaryView, WaitingView } from "./ReviewViews";
import {
  applyReview,
  clearSession,
  createReviewSession,
  currentCard,
  isReviewComplete,
  loadSession,
  nextReadyAt,
  saveSession,
  sessionProgress,
  upcomingCards,
  type ReviewSessionData,
} from "./session";
import type { RecallConfidence } from "./types";

export function FlashcardsReviewPage() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const startMixed = params.get("start") === "mixed";
  const returnToParam = safeReturnTo(params.get("returnTo"));

  // A mixed start replaces whatever session was saved, so the old one is never shown meanwhile.
  const [session, setSession] = useState<ReviewSessionData | null>(() =>
    startMixed ? null : loadSession(),
  );
  const [boot, setBoot] = useState<"loading" | "empty" | null>(startMixed ? "loading" : null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const reviewMutation = useReviewCard();
  const { mutateAsync: fetchQueue } = useStartQueue();
  const current = session ? currentCard(session, nowMs) : null;
  const waiting = !!session && !isReviewComplete(session) && !current;
  const readyAt = waiting ? nextReadyAt(session) : null;

  const bootstrapped = useRef(false);
  useEffect(() => {
    if (!startMixed || bootstrapped.current) return;
    bootstrapped.current = true;
    fetchQueue({ limit: mixedLimit(params.get("limit")) })
      .then((result) => {
        if (result.cards.length === 0) {
          setBoot("empty");
          return;
        }
        const next = createReviewSession(
          result.cards,
          {},
          { mixed: true, ...(returnToParam ? { returnTo: returnToParam } : {}) },
        );
        saveSession(next);
        setSession(next);
        setBoot(null);
        navigate("/flashcards/review", { replace: true });
      })
      .catch((error: unknown) => {
        toast.error(messageForError(error, t));
        setBoot("empty");
      });
  }, [startMixed, params, returnToParam, fetchQueue, navigate, toast, t]);

  useEffect(() => {
    if (!waiting) return;
    const updateTime = () => setNowMs(Date.now());
    const timer = window.setInterval(updateTime, 1000);
    document.addEventListener("visibilitychange", updateTime);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", updateTime);
    };
  }, [waiting]);

  useEffect(() => {
    if (session) saveSession(session);
  }, [session]);

  async function rate(rating: Rating, confidence: RecallConfidence | null) {
    if (!session || !current || reviewMutation.isPending) return;
    const cardId = current.id;
    try {
      const res = await reviewMutation.mutateAsync({
        cardId,
        rating,
        ...(confidence !== null ? { confidence } : {}),
      });
      const reviewedAt = Date.now();
      setNowMs(reviewedAt);
      setSession((prev) =>
        prev
          ? applyReview(
              prev,
              cardId,
              rating,
              res.state.dueAt,
              res.previews,
              rating === "again" ? (res.requeueInMinutes ?? RELEARN_DELAY_MS / 60_000) : 0,
              reviewedAt,
            )
          : prev,
      );
    } catch (error) {
      toast.error(messageForError(error, t));
    }
  }

  function exitTo(to: string) {
    clearSession();
    navigate(to);
  }

  if (boot === "loading") {
    return (
      <div className="mx-auto max-w-xl">
        <h1 className="sr-only">{t("flashcards.review.pageTitleMixed")}</h1>
        <Spinner size="md" label={t("common.loading")} />
      </div>
    );
  }
  if (boot === "empty") {
    return (
      <EmptyView
        pageTitle={t("flashcards.review.pageTitleMixed")}
        returnTo={returnToParam}
        onGo={(to) => navigate(to)}
      />
    );
  }
  if (!session) return <Navigate to="/flashcards" replace />;

  const pageTitle = session.mixed
    ? t("flashcards.review.pageTitleMixed")
    : session.chapter !== undefined
      ? t("flashcards.review.pageTitleChapter", { chapter: session.chapter })
      : t("flashcards.review.pageTitle");

  if (isReviewComplete(session)) {
    return <SummaryView session={session} pageTitle={pageTitle} onExit={exitTo} />;
  }
  if (!current) {
    return (
      <WaitingView
        pageTitle={pageTitle}
        readyAt={readyAt}
        nowMs={nowMs}
        onBack={() => navigate("/flashcards")}
      />
    );
  }

  const { done, total } = sessionProgress(session);
  const progress = t("flashcards.review.progress", { done, total });
  return (
    <div className="mx-auto max-w-xl">
      <h1 className="sr-only">{pageTitle}</h1>
      {session.mixed && (
        <p className="mb-2 text-xs font-medium text-accent-700 dark:text-accent-300">
          {t("flashcards.mixed.title")}
        </p>
      )}
      <div className="mb-2 flex items-center justify-between text-sm text-ink-600 dark:text-ink-400">
        <CardBadges card={current} />
        <span className="tabular-nums">
          {t("flashcards.review.remaining", { count: session.order.length })}
        </span>
      </div>
      <ProgressBar
        className="mb-4"
        value={total === 0 ? 0 : done / total}
        ariaLabel={progress}
        caption={progress}
        captionHidden
        size="sm"
      />

      <ReviewStep
        key={`${current.id}#${session.reviewed}`}
        card={current}
        busy={reviewMutation.isPending}
        onRate={(rating, confidence) => void rate(rating, confidence)}
      />

      {session.mixed && <QueuePanel cards={upcomingCards(session, current.id)} />}
    </div>
  );
}

export default FlashcardsReviewPage;
